/**
 * UI-Helpers für ApiError-Display (Phase 3.5 §2).
 *
 * - `describeError(err)`: deutsche User-Message + Severity + ggf. Hint
 * - `showToast(level, message)`: minimaler Toast-Renderer im #toast-host
 *
 * Toast-Pattern bewusst minimal: keine Animation-Lib, keine externe Dep.
 * In §5 können einzelne Views eigene Inline-Errors zeigen statt Toast (z.B.
 * Conflict-Dialog mit current_state-Vergleich).
 */

import { ApiError } from "./client";
import { escapeHtml } from "../lib/html";

export type ErrorDescription = {
  title: string;
  message: string;
  level: "warning" | "error";
  /** Optional: konkreter Hint was User tun kann (z.B. "Seite neu laden"). */
  hint?: string;
  /** Audit-ID für Support-Anfragen (bei 500er). */
  auditId?: string;
};

/**
 * Mapped einen ApiError auf eine User-friendly DE-Description.
 *
 * Status-Code-Mapping aus Auftragsbrief §2:
 *   - 401 → handled by client.ts (Auto-Logout), sollte hier nicht ankommen
 *   - 403 → "Keine Berechtigung"
 *   - 404 → "Nicht gefunden" (z.B. Meeting_ID unbekannt)
 *   - 409 → "Konflikt" mit current_state-Hint
 *   - 422 → "Edit-Window überschritten" mit Server-Message
 *   - 500 → "Server-Fehler — Drüben kontaktieren" + audit_id
 *   - 0   → "Backend nicht erreichbar" (Netzwerk)
 *   - Sonst → generisch
 */
export function describeError(err: ApiError): ErrorDescription {
  const auditId = err.auditId;
  const base = auditId ? { auditId } : {};

  // Worker-spezifische Codes (überschreiben Status-Default)
  switch (err.code) {
    case "AUTH_FAILED":
    case "AUTH_EXPIRED":
    case "NO_TOKEN":
      return {
        title: "Nicht eingeloggt",
        message: err.message,
        level: "warning",
        hint: "Du wirst zum Login weitergeleitet."
      };

    case "BEREICH_NOT_ALLOWED":
      return {
        title: "Bereich nicht freigegeben",
        message: err.message,
        level: "error",
        hint: "Bitte mit Bereichsleitung klären."
      };

    case "ROLE_NOT_ALLOWED":
      return {
        title: "Rolle nicht ausreichend",
        message: err.message,
        level: "error",
        hint: "Funktion ist für deine Rolle nicht freigegeben."
      };

    case "OWN_ONLY":
      return {
        title: "Nur eigene Daten",
        message: err.message,
        level: "error",
        hint: "Du darfst nur deine eigenen Einträge ändern."
      };

    case "PERMISSION_DENIED":
      return {
        title: "Keine Berechtigung",
        message: err.message,
        level: "error"
      };

    case "EDIT_WINDOW_EXCEEDED":
      return {
        title: "Edit-Window überschritten",
        message: err.message,
        level: "warning",
        hint: "Bitte BL bitten den Eintrag zu ändern (Override)."
      };

    // 🔑 Der Hinweis versprach bis 21.08.2026 einen Override-Knopf, den es NIE
    // gab: den Conflict-Check umgeht allein `context.source = "app_overwrite"`,
    // und kein einziger Frontend-Aufruf setzt das (`contextSource` kommt in
    // frontend/src nur als Typ und in der Audit-Anzeige vor). Belegt am
    // Audit-Log: unter 862 Outcome-Events steht kein einziges `__OVERWRITE`.
    // Ein 409 heißt hier: der Eintrag trägt ein echtes Ergebnis — das ist eine
    // Sperre, keine Rückfrage.
    case "CONFLICT":
      // 🔒 Zeeg-Sperre (20.10.2026, `src/lib/zeeg-sperre.ts`): kein Ergebnis steht
      // im Weg, sondern Zeeg — der Hinweis unten wäre hier falsch.
      if (err.field === "zeeg_termin") {
        return {
          title: "Zeeg-Termin — bitte in Zeeg ändern",
          message: err.message,
          level: "warning",
          hint: "Zeeg-Termine werden nur in Zeeg verschoben, damit der Verein die richtige Einladung bekommt. Die App übernimmt die Änderung automatisch."
        };
      }
      return {
        title: "Konflikt",
        message: err.message,
        level: "warning",
        hint: "Hier steht bereits ein Ergebnis — das lässt sich nicht überschreiben. Wenn es falsch ist, bitte an einen Bereichsleiter wenden."
      };

    case "NOT_FOUND":
      return {
        title: "Nicht gefunden",
        message: err.message,
        level: "warning",
        hint: "Vielleicht wurde der Eintrag in der Zwischenzeit gelöscht. Seite neu laden."
      };

    case "VALIDATION_FAILED": {
      const issuesText = err.issues
        ? err.issues.map((i) => `${i.field}: ${i.message}`).join("\n")
        : err.message;
      return {
        title: "Eingabe ungültig",
        message: issuesText,
        level: "warning"
      };
    }

    case "NETWORK_ERROR":
      return {
        title: "Backend nicht erreichbar",
        message: err.message,
        level: "error",
        hint: "Internet-Verbindung prüfen und Seite neu laden."
      };

    case "INVALID_RESPONSE":
      return {
        title: "Unerwartete Server-Antwort",
        message: err.message,
        level: "error",
        ...base
      };
  }

  // Status-Code-Fallback
  if (err.statusCode >= 500) {
    return {
      title: "Server-Fehler",
      message: err.message || "Unbekannter Server-Fehler — bitte Drüben kontaktieren.",
      level: "error",
      ...base
    };
  }
  if (err.statusCode === 403) {
    return { title: "Keine Berechtigung", message: err.message, level: "error" };
  }
  if (err.statusCode === 0) {
    return {
      title: "Backend nicht erreichbar",
      message: err.message,
      level: "error",
      hint: "Internet-Verbindung prüfen."
    };
  }

  return {
    title: "Fehler",
    message: err.message || `HTTP ${err.statusCode} (${err.code})`,
    level: "error",
    ...base
  };
}

// =============================================================================
// Toast-Renderer (minimal — im #toast-host am Body-Ende, kommt vollständig
// mit §3 + Styles in global.css)
// =============================================================================

const TOAST_HOST_ID = "toast-host";
const TOAST_TIMEOUT_MS = 5000;

export function showToast(description: ErrorDescription): void {
  const host = ensureToastHost();
  const el = document.createElement("div");
  el.className = `toast toast-${description.level}`;
  el.setAttribute("role", description.level === "error" ? "alert" : "status");

  const parts: string[] = [];
  parts.push(`<strong>${escapeHtml(description.title)}</strong>`);
  parts.push(`<span>${escapeHtml(description.message)}</span>`);
  if (description.hint) {
    parts.push(`<small>${escapeHtml(description.hint)}</small>`);
  }
  if (description.auditId) {
    parts.push(`<code style="font-size:11px;opacity:0.7;">Audit: ${escapeHtml(description.auditId)}</code>`);
  }
  el.innerHTML = parts.join("");

  host.appendChild(el);
  window.setTimeout(() => el.remove(), TOAST_TIMEOUT_MS);
}

/** Convenience: show toast direct from ApiError. */
export function toastApiError(err: ApiError): void {
  showToast(describeError(err));
}

/** Convenience: plain non-Error-Toast (z.B. nach erfolgreichem Save). */
export function pushToast(
  title: string,
  message: string,
  level: "warning" | "error" | "success" = "success"
): void {
  showToast({
    title,
    message,
    level: level === "success" ? "warning" : level
  });
  // CSS-Variante via class toast-success
  const host = document.getElementById("toast-host");
  const last = host?.lastElementChild as HTMLElement | null;
  if (last && level === "success") {
    last.classList.remove("toast-warning");
    last.classList.add("toast-success");
  }
}

function ensureToastHost(): HTMLElement {
  let host = document.getElementById(TOAST_HOST_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = TOAST_HOST_ID;
    document.body.appendChild(host);
  }
  return host;
}

